/**
 * The audit register and heads panel for the auditor (wave AUD frontend items 2 and 3):
 *   GET /api/audit
 *   GET /api/audit/heads
 *
 * Author: Mahmoud Ragab Ahmed.
 *
 * Offers every filter the API accepts (family, object type, object id, actor, event,
 * since, until) and paging (limit, offset, total). Renders the per-family chain verdict
 * as a badge row ("intact", or "broken at seq N: reason") with any broken family first,
 * renders the 422 audit.range_empty refusal in the server's own words, and renders the
 * heads panel with a "Download heads" control that saves the JSON exactly as the API
 * returned it (the file the offline verifier reads).
 * Anyone without AUDIT_LOG READ is answered 404 (absence) and sees the absence panel.
 */

import { useMemo, useState } from "react";

import {
  AUDIT_EVENT_TYPES,
  AUDIT_FAMILIES,
  getAuditHeads,
  getAuditRegister,
  type AuditEntry,
  type AuditHead,
  type ChainVerdict,
} from "../api/audit";
import { ApiError } from "../api/client";
import { useToken } from "../auth/sessionContext";
import { strings, term } from "../brand/strings";
import { formatAuditChange } from "../components/HistoryDrawer";
import { DataTable, type RegisterColumn } from "@/components/ui/data-table";
import { zonedStamp } from "../lib/zonedTime";
import { AbsencePanel, EmptyState, Pagination, RefusalBox, Section, SkeletonRows } from "./parts";
import { useQuery } from "./useQuery";

const DEFAULT_LIMIT = 50;
const LIMIT_CHOICES = [25, 50, 100, 200] as const;
const HASH_PREFIX_LENGTH = 12;

/** Sort chain verdicts so any broken family (!intact) is listed before intact families. */
export function sortChainVerdicts(chains: readonly ChainVerdict[]): ChainVerdict[] {
  return [...chains].sort((a, b) => {
    if (a.intact === b.intact) {
      return 0;
    }
    return a.intact ? 1 : -1;
  });
}

function familyLabel(family: string): string {
  return term(family);
}

function eventLabel(eventType: string): string {
  return term(eventType);
}

function actorLabel(entry: AuditEntry): string {
  if (entry.actorDisplayName !== null && entry.actorDisplayName !== "") {
    return entry.actorDisplayName;
  }
  if (entry.actorMechanism !== null && entry.actorMechanism !== "") {
    return entry.actorMechanism;
  }
  return strings.audit.unknownActor;
}

function verdictText(verdict: ChainVerdict): string {
  if (verdict.intact) {
    return strings.audit.chainIntact;
  }
  return strings.audit.chainBroken(verdict.brokenAtSeq, verdict.reason);
}

/** Save the verbatim JSON returned by GET /api/audit/heads for the offline verifier. */
export function downloadHeadsJson(rawJson: string): void {
  const blob = new Blob([rawJson], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = "audit-heads.json";
  anchor.click();
  URL.revokeObjectURL(url);
}

/**
 * Wave AUD frontend item 3: the heads panel on the audit register.
 * Reads GET /api/audit/heads and renders a table (family, head seq, head hash prefix,
 * genesis instant, count) with a "Download heads" control that saves the verbatim JSON.
 */
export function AuditHeadsPanel(): React.JSX.Element | null {
  const token = useToken();
  const copy = strings.audit;
  const state = useQuery((signal) => getAuditHeads(token, signal), [token]);
  const columns = useMemo(() => headColumns(), []);

  if (
    state.kind === "failed" &&
    state.error instanceof ApiError &&
    state.error.kind === "absence"
  ) {
    return <AbsencePanel />;
  }

  return (
    <Section heading={copy.headsHeading} lede={copy.headsLede}>
      {state.kind === "loading" && <SkeletonRows rows={3} columns={5} />}
      {state.kind === "ready" && (
        <div data-testid="audit-heads-panel">
          <div className="q-actions-row mb-3">
            <button
              type="button"
              className="q-button q-button--quiet"
              data-testid="audit-download-heads"
              onClick={() => {
                downloadHeadsJson(state.data.rawJson);
              }}
            >
              {copy.downloadHeads}
            </button>
          </div>
          {state.data.families.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="audit-heads-empty">
              {copy.noHeads}
            </p>
          ) : (
            <DataTable
              data={state.data.families}
              columns={columns}
              caption={copy.headsCaption}
              testId="audit-heads-table"
              getRowId={(row) => row.family}
              getRowTestId={() => "audit-heads-row"}
              pageSize={50}
              footer={false}
            />
          )}
        </div>
      )}
    </Section>
  );
}

export function AuditPage(): React.JSX.Element {
  const token = useToken();
  const copy = strings.audit;

  const [family, setFamily] = useState("");
  const [objectType, setObjectType] = useState("");
  const [objectId, setObjectId] = useState("");
  const [actorPersonId, setActorPersonId] = useState("");
  const [eventType, setEventType] = useState("");
  const [since, setSince] = useState("");
  const [until, setUntil] = useState("");
  const [limit, setLimit] = useState<number>(DEFAULT_LIMIT);
  const [offset, setOffset] = useState(0);

  const state = useQuery(
    (signal) =>
      getAuditRegister(
        token,
        {
          family,
          objectType: objectType.trim(),
          objectId: objectId.trim(),
          actorPersonId: actorPersonId.trim(),
          eventType,
          since: since.trim(),
          until: until.trim(),
          limit,
          offset,
        },
        signal,
      ),
    [token, family, objectType, objectId, actorPersonId, eventType, since, until, limit, offset],
  );

  const columns = useMemo(() => registerColumns(), []);

  if (
    state.kind === "failed" &&
    state.error instanceof ApiError &&
    state.error.kind === "absence"
  ) {
    return <AbsencePanel />;
  }

  const anyFilter =
    family !== "" ||
    objectType.trim() !== "" ||
    objectId.trim() !== "" ||
    actorPersonId.trim() !== "" ||
    eventType !== "" ||
    since.trim() !== "" ||
    until.trim() !== "";

  const sortedChains = state.kind === "ready" ? sortChainVerdicts(state.data.chains) : [];

  return (
    <div className="q-ui" data-testid="audit-page">
      <h2 className="q-shell__heading">{copy.heading}</h2>
      <p className="q-shell__lede">{copy.lede}</p>

      <Section heading={copy.chainsHeading}>
        {state.kind === "loading" && <SkeletonRows rows={2} columns={4} />}
        {state.kind === "ready" && (
          <div
            className="flex flex-wrap gap-2"
            role="list"
            aria-label={copy.chainsHeading}
            data-testid="audit-verdicts"
          >
            {sortedChains.map((verdict) => {
              const text = verdictText(verdict);
              return (
                <span
                  key={verdict.family}
                  role="listitem"
                  className={verdict.intact ? "q-chip q-chip--success" : "q-chip q-chip--danger"}
                  data-testid="audit-verdict-badge"
                  data-family={verdict.family}
                  data-intact={String(verdict.intact)}
                >
                  {`${familyLabel(verdict.family)}: ${text}`}
                </span>
              );
            })}
          </div>
        )}
      </Section>

      <AuditHeadsPanel />

      <Section heading={copy.heading}>
        <div
          className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4"
          data-testid="audit-filters"
        >
          <div className="q-field">
            <label className="q-field__label" htmlFor="audit-filter-family">
              {copy.familyLabel}
            </label>
            <select
              id="audit-filter-family"
              data-testid="audit-filter-family"
              className="q-input"
              value={family}
              onChange={(event) => {
                setFamily(event.target.value);
                setOffset(0);
              }}
            >
              <option value="">{copy.familyAll}</option>
              {AUDIT_FAMILIES.map((one) => (
                <option key={one} value={one}>
                  {familyLabel(one)}
                </option>
              ))}
            </select>
          </div>

          <div className="q-field">
            <label className="q-field__label" htmlFor="audit-filter-object-type">
              {copy.objectTypeLabel}
            </label>
            <input
              id="audit-filter-object-type"
              data-testid="audit-filter-object-type"
              type="text"
              className="q-input"
              placeholder={copy.objectTypePlaceholder}
              value={objectType}
              onChange={(event) => {
                setObjectType(event.target.value);
                setOffset(0);
              }}
            />
          </div>

          <div className="q-field">
            <label className="q-field__label" htmlFor="audit-filter-object-id">
              {copy.objectIdLabel}
            </label>
            <input
              id="audit-filter-object-id"
              data-testid="audit-filter-object-id"
              type="text"
              className="q-input"
              placeholder={copy.objectIdPlaceholder}
              value={objectId}
              onChange={(event) => {
                setObjectId(event.target.value);
                setOffset(0);
              }}
            />
          </div>

          <div className="q-field">
            <label className="q-field__label" htmlFor="audit-filter-actor">
              {copy.actorLabel}
            </label>
            <input
              id="audit-filter-actor"
              data-testid="audit-filter-actor"
              type="text"
              className="q-input"
              placeholder={copy.actorPlaceholder}
              value={actorPersonId}
              onChange={(event) => {
                setActorPersonId(event.target.value);
                setOffset(0);
              }}
            />
          </div>

          <div className="q-field">
            <label className="q-field__label" htmlFor="audit-filter-event">
              {copy.eventLabel}
            </label>
            <select
              id="audit-filter-event"
              data-testid="audit-filter-event"
              className="q-input"
              value={eventType}
              onChange={(event) => {
                setEventType(event.target.value);
                setOffset(0);
              }}
            >
              <option value="">{copy.eventAll}</option>
              {AUDIT_EVENT_TYPES.map((one) => (
                <option key={one} value={one}>
                  {eventLabel(one)}
                </option>
              ))}
            </select>
          </div>

          <div className="q-field">
            <label className="q-field__label" htmlFor="audit-filter-since">
              {copy.sinceLabel}
            </label>
            <input
              id="audit-filter-since"
              data-testid="audit-filter-since"
              type="text"
              className="q-input"
              value={since}
              onChange={(event) => {
                setSince(event.target.value);
                setOffset(0);
              }}
            />
          </div>

          <div className="q-field">
            <label className="q-field__label" htmlFor="audit-filter-until">
              {copy.untilLabel}
            </label>
            <input
              id="audit-filter-until"
              data-testid="audit-filter-until"
              type="text"
              className="q-input"
              value={until}
              onChange={(event) => {
                setUntil(event.target.value);
                setOffset(0);
              }}
            />
          </div>

          <div className="q-field">
            <label className="q-field__label" htmlFor="audit-filter-limit">
              {copy.limitLabel}
            </label>
            <select
              id="audit-filter-limit"
              data-testid="audit-filter-limit"
              className="q-input"
              value={String(limit)}
              onChange={(event) => {
                setLimit(Number(event.target.value));
                setOffset(0);
              }}
            >
              {LIMIT_CHOICES.map((one) => (
                <option key={one} value={String(one)}>
                  {String(one)}
                </option>
              ))}
            </select>
          </div>
        </div>

        {anyFilter && (
          <div className="q-actions-row mb-3">
            <button
              type="button"
              className="q-button q-button--quiet"
              data-testid="audit-filter-clear"
              onClick={() => {
                setFamily("");
                setObjectType("");
                setObjectId("");
                setActorPersonId("");
                setEventType("");
                setSince("");
                setUntil("");
                setOffset(0);
              }}
            >
              {copy.clearFilters}
            </button>
          </div>
        )}

        {state.kind === "loading" && <SkeletonRows rows={6} columns={6} />}

        {state.kind === "failed" &&
          state.error instanceof ApiError &&
          (state.error.kind === "invalid" ||
            state.error.kind === "conflict" ||
            state.error.kind === "forbidden") && <RefusalBox error={state.error} />}

        {state.kind === "failed" &&
          (!(state.error instanceof ApiError) ||
            (state.error.kind !== "invalid" &&
              state.error.kind !== "conflict" &&
              state.error.kind !== "forbidden" &&
              state.error.kind !== "absence")) && (
            <div className="q-refusal" role="alert">
              <p className="q-refusal__heading">{strings.common.failed}</p>
            </div>
          )}

        {state.kind === "ready" && state.data.items.length === 0 && (
          <EmptyState title={copy.emptyTitle} body={copy.emptyBody} assignmentAware={false} />
        )}

        {state.kind === "ready" && state.data.items.length > 0 && (
          <div data-testid="audit-register">
            <DataTable
              data={state.data.items}
              columns={columns}
              caption={copy.entriesCaption}
              testId="audit-register-table"
              getRowId={(row) => row.id}
              getRowTestId={() => "audit-register-row"}
              pageSize={limit}
              footer={false}
            />
            <Pagination
              total={state.data.total}
              limit={state.data.limit}
              offset={state.data.offset}
              onMove={setOffset}
            />
          </div>
        )}
      </Section>
    </div>
  );
}

function headColumns(): RegisterColumn<AuditHead>[] {
  const copy = strings.audit;
  return [
    {
      id: "family",
      label: copy.headFamilyColumn,
      text: (row) => familyLabel(row.family),
      render: (row) => <span data-testid="audit-head-family">{familyLabel(row.family)}</span>,
    },
    {
      id: "headSeq",
      label: copy.headSeqColumn,
      text: (row) => String(row.headSeq),
      sortValue: (row) => row.headSeq,
      render: (row) => (
        <span className="font-mono text-xs" data-testid="audit-head-seq">
          {String(row.headSeq)}
        </span>
      ),
    },
    {
      id: "headHash",
      label: copy.headHashColumn,
      text: (row) => row.headHash.slice(0, HASH_PREFIX_LENGTH),
      render: (row) => (
        <span className="font-mono text-xs" title={row.headHash} data-testid="audit-head-hash">
          {row.headHash.slice(0, HASH_PREFIX_LENGTH)}
        </span>
      ),
    },
    {
      id: "genesisAt",
      label: copy.headGenesisColumn,
      text: (row) => (row.genesisAt === null ? "" : zonedStamp(row.genesisAt)),
      sortValue: (row) => row.genesisAt ?? "",
      className: "whitespace-nowrap",
      render: (row) => (
        <span data-testid="audit-head-genesis">
          {row.genesisAt === null ? "" : zonedStamp(row.genesisAt)}
        </span>
      ),
    },
    {
      id: "count",
      label: copy.headCountColumn,
      text: (row) => String(row.count),
      sortValue: (row) => row.count,
      render: (row) => (
        <span className="font-mono text-xs" data-testid="audit-head-count">
          {String(row.count)}
        </span>
      ),
    },
  ];
}

function registerColumns(): RegisterColumn<AuditEntry>[] {
  const copy = strings.audit;
  return [
    {
      id: "family",
      label: copy.columnFamily,
      text: (row) => `${familyLabel(row.family)} #${String(row.seq)}`,
      sortValue: (row) => `${row.family}-${String(row.seq).padStart(9, "0")}`,
      render: (row) => (
        <span data-testid="audit-row-family">
          {familyLabel(row.family)}{" "}
          <span className="font-mono text-xs text-muted-foreground">{`#${String(row.seq)}`}</span>
        </span>
      ),
    },
    {
      id: "when",
      label: copy.columnWhen,
      text: (row) => zonedStamp(row.createdAt),
      sortValue: (row) => row.createdAt,
      className: "whitespace-nowrap",
      render: (row) => <span data-testid="audit-row-when">{zonedStamp(row.createdAt)}</span>,
    },
    {
      id: "actor",
      label: copy.columnActor,
      text: actorLabel,
      render: (row) => <span data-testid="audit-row-actor">{actorLabel(row)}</span>,
    },
    {
      id: "event",
      label: copy.columnEvent,
      text: (row) => eventLabel(row.eventType),
      render: (row) => <span data-testid="audit-row-event">{eventLabel(row.eventType)}</span>,
    },
    {
      id: "object",
      label: copy.columnObject,
      text: (row) =>
        [
          row.objectType,
          row.objectId ?? "",
          row.parentType === null ? "" : copy.historyPart(row.objectType, row.parentType),
        ]
          .filter((one) => one !== "")
          .join(" "),
      render: (row) => (
        <span data-testid="audit-row-object">
          <span className="font-mono text-xs">{row.objectType}</span>
          {row.objectId !== null && (
            <span className="ms-1 font-mono text-xs text-muted-foreground">{row.objectId}</span>
          )}
          {row.parentType !== null && (
            <span className="ms-1.5 q-chip" data-testid="audit-row-part">
              {copy.historyPart(row.objectType, row.parentType)}
            </span>
          )}
        </span>
      ),
    },
    {
      id: "changes",
      label: copy.columnChanges,
      text: (row) => row.changes.map(formatAuditChange).join("; "),
      className: "min-w-[16rem]",
      render: (row) => (
        <span className="font-mono text-xs" data-testid="audit-row-changes">
          {row.changes.map(formatAuditChange).join("; ")}
        </span>
      ),
    },
  ];
}
